import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, linkedSignal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';
import { MessagesService, UserService } from 'shared/domain';

import { OrganisationSettings } from '@treetop/shared/model';

import { OrganisationSettingsHttpService } from './organisation-settings.http.service';
import { tapAllResponses } from './rxjs.util';
import { GuidelineSyncService } from './services/guideline-sync.service';

@Injectable({ providedIn: 'root' })
export class OrganisationSettingsService {
  private organisationSettingsHttpService = inject(OrganisationSettingsHttpService);
  private messagesService = inject(MessagesService);
  private userService = inject(UserService);
  private guidelineSyncService = inject(GuidelineSyncService);

  private organisationSettings = linkedSignal<OrganisationSettings | null>(() => {
    return {
      ...this.userService.user()?.currentOrganisation.organisationSettings,
      customisations: {
        colorPresets: this.userService.user()?.currentOrganisation.organisationSettings?.customisations?.colorPresets ?? [],
        flowchart: this.userService.user()?.currentOrganisation.organisationSettings?.customisations?.flowchart ?? {},
      },
    };
  });

  public updateOrganisationSettings = rxMethod<Partial<OrganisationSettings>>((settings$) =>
    settings$.pipe(
      switchMap((settings) => {
        const previousSettings = this.organisationSettings();
        const optimisticSettings = { ...this.organisationSettings(), ...settings } as OrganisationSettings;
        this.organisationSettings.set(optimisticSettings);

        return this.organisationSettingsHttpService.setOrganisationSettings(optimisticSettings).pipe(
          tapAllResponses<OrganisationSettings, HttpErrorResponse>(
            (response) => {
              this.organisationSettings.set(response);
              this.guidelineSyncService.syncGuidelinesForCurrentUser();
              this.messagesService.showInfo('SettingsPage.OrganisationSettingsTab.OrganisationSettingsChanged');
            },
            (error) => {
              this.organisationSettings.set(previousSettings);
              this.messagesService.showError(undefined, error);
            },
          ),
        );
      }),
    ),
  );

  public settings = computed<OrganisationSettings>(() => this.organisationSettings());
}
